/**
 * PRD.291 — Today's Prescriptions → Create Invoice drawer.
 *
 * Two steps inside one Billing-width drawer:
 *   Step 1 (select) — list the prescription's inventory-bound medicines with
 *          checkboxes (all not-yet-invoiced selected by default). Already-billed
 *          lines show disabled as "Invoiced". No writes happen here.
 *   Step 2 (bill)  — mount the EXISTING Billing surface (`BillingInvoicesPanel`)
 *          which creates a fresh draft invoice and adds exactly the selected
 *          prescription lines via the existing "Add Items from Prescription"
 *          handler (`autoAddPrescriptionIds`). No pharmacy-specific billing
 *          logic; payments/receipts all reuse the Billing components.
 *
 * Never navigates to `/patient/:id`.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import {
  qPrescriptionInvoiceLinesByEncounter,
  type TodaysPrescriptionLineRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { useBrandTokens } from '../../shell/shared';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { BillingInvoicesPanel } from '../patient/encounters/TreatmentInvoiceDrawer';
import { DRAWER_WIDTH_BY_PRESET } from '../patient/encounters/TreatmentChartingFilesDrawer';
import type { TreatmentRecordDrawerWidth } from '@helios/data-access';

export interface TodaysPrescriptionInvoiceContext {
  encounterId: string;
  patientId: string;
  patientName: string | null;
  /** Prescription/visit date (YYYY-MM-DD) to scope Billing to. */
  date: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: TodaysPrescriptionInvoiceContext | null;
  drawerWidth: TreatmentRecordDrawerWidth;
}

function lineDisplayName(r: TodaysPrescriptionLineRow): string {
  return (
    r.item_name_snapshot?.trim() || r.free_text_name?.trim() || r.item_name?.trim() || 'Medicine'
  );
}

function lineDetails(r: TodaysPrescriptionLineRow): string {
  return (
    r.item_details?.trim() ||
    [r.brand_name_snapshot, r.generic_name_snapshot, r.strength_snapshot, r.dosage_form_snapshot]
      .filter(Boolean)
      .join(' · ')
  );
}

export function TodaysPrescriptionInvoiceDrawer({
  open,
  onOpenChange,
  context,
  drawerWidth,
}: Props) {
  const brandTokens = useBrandTokens();
  const fmt = useFormatMoney();
  const encounterId = context?.encounterId ?? '';

  const { data: lineRows = [] } = usePowerSyncQuery<TodaysPrescriptionLineRow>(
    qPrescriptionInvoiceLinesByEncounter(encounterId),
  );

  const [step, setStep] = useState<'select' | 'bill'>('select');
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [confirmedIds, setConfirmedIds] = useState<string[]>([]);

  // Lines that can still be billed (not already invoiced).
  const billable = useMemo(() => lineRows.filter((r) => r.already_invoiced !== 1), [lineRows]);
  const invoiced = useMemo(() => lineRows.filter((r) => r.already_invoiced === 1), [lineRows]);

  // Reset on open; default every billable line checked once lines resolve.
  useEffect(() => {
    if (!open) {
      setStep('select');
      setChecked({});
      setConfirmedIds([]);
    }
  }, [open, context?.encounterId]);
  useEffect(() => {
    if (!open || step !== 'select') return;
    setChecked((prev) => {
      // Only seed defaults the first time (empty map) so user toggles persist.
      if (Object.keys(prev).length > 0) return prev;
      const next: Record<string, boolean> = {};
      for (const r of billable) next[r.prescription_id] = true;
      return next;
    });
  }, [open, step, billable]);

  const selectedIds = useMemo(
    () => billable.filter((r) => checked[r.prescription_id]).map((r) => r.prescription_id),
    [billable, checked],
  );

  const toggle = (id: string) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  const footer =
    step === 'select' ? (
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Button variant="outline" size="md" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          variant="gradient"
          size="md"
          disabled={selectedIds.length === 0}
          onClick={() => {
            setConfirmedIds(selectedIds);
            setStep('bill');
          }}
        >
          Continue{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
        </Button>
      </div>
    ) : undefined;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Create Invoice"
      description={
        context?.patientName
          ? step === 'select'
            ? `Select medicines to invoice for ${context.patientName}.`
            : `Billing — ${context.patientName}`
          : undefined
      }
      portalStyle={brandTokens as CSSProperties}
      width={DRAWER_WIDTH_BY_PRESET[drawerWidth]}
      footer={footer}
    >
      {step === 'select' ? (
        <div
          style={{
            padding: 'var(--sp-5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            All prescribed medicines are selected by default. Uncheck any you don't want to invoice
            yet — they stay in Today's Prescriptions.
          </div>

          {billable.length === 0 && invoiced.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: 'var(--sp-8)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                border: '2px dashed var(--border)',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              No inventory medicines on this prescription to invoice.
            </div>
          ) : (
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
              }}
            >
              {billable.map((r) => {
                const details = lineDetails(r);
                return (
                  <label
                    key={r.prescription_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-3)',
                      padding: 'var(--sp-3)',
                      borderBottom: '1px solid var(--border)',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!checked[r.prescription_id]}
                      onChange={() => toggle(r.prescription_id)}
                      style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text)',
                        }}
                      >
                        {lineDisplayName(r)}
                      </div>
                      {details && (
                        <div
                          style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}
                        >
                          {details}
                        </div>
                      )}
                    </div>
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text-muted)',
                        flexShrink: 0,
                      }}
                    >
                      {r.inventory_selling_price != null ? fmt(r.inventory_selling_price) : '—'}
                    </span>
                  </label>
                );
              })}

              {/* Already-invoiced lines — shown for context, not selectable. */}
              {invoiced.map((r) => (
                <div
                  key={r.prescription_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-3)',
                    padding: 'var(--sp-3)',
                    borderBottom: '1px solid var(--border)',
                    opacity: 0.6,
                  }}
                >
                  <input type="checkbox" checked disabled style={{ width: 18, height: 18 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text)',
                      }}
                    >
                      {lineDisplayName(r)}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-semi)',
                      color: 'var(--color-success-text, #166534)',
                      flexShrink: 0,
                    }}
                  >
                    Invoiced
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : context ? (
        // Step 2 — the existing Billing surface. Creating the invoice + adding
        // the selected lines happens inside InvoicesTab (autoAddPrescriptionIds).
        <BillingInvoicesPanel
          active={step === 'bill'}
          patientId={context.patientId}
          initialEncounterId={context.encounterId}
          initialDate={context.date}
          autoAddPrescriptionIds={confirmedIds}
        />
      ) : null}
    </Drawer>
  );
}
